import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { DocumentEditOwner } from '../documentOwner';

type MutationInput = {
  owner: DocumentEditOwner;
  documentId: string;
};

export function useDeleteDocument(documentTypePath: string) {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<unknown, Error, MutationInput>({
    mutationFn: ({ owner, documentId }: MutationInput) => {
      if ('familyId' in owner) {
        return api.delete(`families/${owner.familyId}/${documentTypePath}/${documentId}`);
      }
      if (documentTypePath === 'meeting-agendas') {
        return api.delete(`meeting-agendas/${documentId}`);
      }
      return api.delete(`clients/${owner.clientId}/${documentTypePath}/${documentId}`);
    },
    onSettled: (_data, _error, { owner }) => {
      queryClient.invalidateQueries({ queryKey: [documentTypePath] });
      if ('familyId' in owner) {
        queryClient.invalidateQueries({ queryKey: ['families', owner.familyId, 'all-documents'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['clients', owner.clientId, 'all-documents'] });
      }
    },
  });

  const deleteDocument = async (owner: DocumentEditOwner, documentId: string) => {
    return mutateAsync({ owner, documentId });
  };

  return {
    deleteDocument,
    error,
    isLoading,
    reset,
  };
}
